import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";

import {
  formatCurrency,
  getSellarCreditLowBalanceInfo,
} from "@sellar-io/common";

import dayjs from "dayjs";

import { useState } from "react";

import { useLegalEntity } from "../../../../../../hooks/api/v2/legalEntity/useLegalEntity";
import { DataButton, H4, Icon } from "@sellar-io/components";
import { useLegalEntityId } from "../../../../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { TwoSellarCreditPayDownTabDialog } from "./TwoSellarCreditPayDownTabDialog";
import { useLegalEntityCompanyCreditInfo } from "../../../../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useSellarCurrentBusiness } from "../../../../../../hooks/useSellarCurrentBusiness.shared";
import { useNavigate } from "react-router";
import { SELLAR_CREDIT_DIRECT_DEBIT } from "../../../../../../constants/routes";

const twoCreditBuilderUrl =
  "https://checkout.two.inc/credit_limit_change/credit_builder";

const twoSignInLink =
  import.meta.env.VITE_ENV === "production"
    ? `https://buyer.two.inc/login`
    : `https://buyer.sandbox.two.inc/login`;

export const SellarCreditTabSummary = () => {
  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });
  const credit = legalEntity?.legalEntityCredit;

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const creditInUse = () => {
    if (!credit || credit.creditAmountInUse === 0) return 0;
    if (credit.creditLimit === 0) return 100;
    return Math.min(
      100,
      Math.round((credit.creditAmountInUse / credit.creditLimit) * 100),
    );
  };

  const creditInUsePercent = creditInUse();

  return (
    <>
      <div
        className={twMerge(
          "flex flex-col gap-2 border-zinc-300 bg-white",
          isDesktop ? "rounded-lg border" : "border-b",
        )}
      >
        <div className="flex flex-col gap-4 px-4 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <H4 className="mb-2">Sellar Credit tab</H4>
            <div className="flex items-center">
              <DataButton
                border
                onClick={() => window.open(twoSignInLink, "_blank")}
                className="font-medium text-zinc-800"
              >
                <Icon icon={"OPEN_IN_NEW"} size={0.7} />
                View Two dashboard
              </DataButton>
            </div>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Spend:</span>
              {formatCurrency(credit?.creditAmountInUse ?? 0)}
              <span className="rounded bg-purple-100 px-1 py-[2px] text-purple-500">
                {creditInUsePercent}%
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="font-semibold">Available balance:</span>
              {formatCurrency(credit?.creditAmountAvailable ?? 0)}
            </div>
          </div>

          <div className="flex gap-1">
            <span
              style={{
                width: `${creditInUsePercent > 0 ? creditInUsePercent : "1"}%`,
              }}
              className={`h-[10px] flex-none rounded bg-purple-200`}
            />
            <span className="h-[10px] w-full rounded bg-gradient-to-br from-[#B557F1] to-[#D213A6]" />
          </div>

          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex items-center gap-2 text-purple-400">
              <p>
                Updated at{" "}
                {dayjs(credit?.lastSyncedWithTwoAt).format(
                  "HH:mm:ss DD/MM/YYYY",
                )}
              </p>
            </div>

            <div className="flex items-center gap-2 text-zinc-400">
              <span className="font-semibold">Credit limit:</span>
              {formatCurrency(credit?.creditLimit ?? 0)}
            </div>
          </div>
        </div>
        <hr className="border-t-[1px] border-zinc-200" />
        <SellarCreditTabPaymentDetails />
      </div>
    </>
  );
};

const SellarCreditTabPaymentDetails = () => {
  const [openPayDownTabDialog, setOpenPayDownTabDialog] = useState(false);
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();

  const { data: companyCreditInfoData } = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id),
    },
  );

  const isSellarCreditBalanceLow = Boolean(
    getSellarCreditLowBalanceInfo({
      creditLimit: companyCreditInfoData?.creditLimit ?? 0,
      availableBalance: companyCreditInfoData?.creditAmountAvailable ?? 0,
    }).data?.isLow,
  );

  const renderIndicator = () => {
    if (isSellarCreditBalanceLow) {
      return (
        <SellarCreditTabPaymentDetailsLowBalanceIndicator
          onClick={() => setOpenPayDownTabDialog(true)}
        />
      );
    }

    // TODO: potentially add other states once we can establish whether buyer has setup dd

    return null;
  };

  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-zinc-800">Payment details</span>
          <p className="text-sm text-zinc-500">
            Paying down your tab early helps build a more detailed repayment
            history with our financial partner Two Inc, which could make you
            eligible for a higher credit limit with them.
          </p>
        </div>
        <div className={twMerge("flex flex-col gap-4")}>
          <>{renderIndicator()}</>
          <div className="flex w-full flex-wrap justify-between gap-4">
            <div className="flex items-center justify-center gap-4">
              <DataButton
                border
                onClick={() => setOpenPayDownTabDialog(true)}
                className="font-medium text-zinc-800"
              >
                Pay down tab
              </DataButton>

              <DataButton
                onClick={() => {
                  navigate(SELLAR_CREDIT_DIRECT_DEBIT);
                }}
                className="font-medium text-zinc-800"
              >
                Set up direct debit
              </DataButton>
            </div>

            <DataButton
              border
              onClick={() => window.open(twoCreditBuilderUrl, "_blank")}
              className="font-medium text-zinc-800"
            >
              Request limit increase
            </DataButton>
          </div>
        </div>
      </div>
      <TwoSellarCreditPayDownTabDialog
        active={openPayDownTabDialog}
        onDismiss={() => setOpenPayDownTabDialog(false)}
      />
    </>
  );
};

const SellarCreditTabPaymentDetailsLowBalanceIndicator = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer flex-col justify-between gap-2 rounded-lg border border-orange-300 bg-orange-100 px-3 py-2 text-left text-sm text-orange-800 sm:flex-row sm:items-center"
    >
      <div className="flex items-center gap-2">
        <div>
          <Icon icon="ALERT" />
        </div>
        <p>
          <span className="font-semibold">Low balance</span>-{" "}
          <span>
            Pay down your tab by paying off individual orders or this month's
            statement
          </span>
        </p>
      </div>
      <div className="hidden items-center gap-2 md:flex">
        <span className="font-bold text-nowrap">Pay down tab</span>
        <Icon icon="CHEVRON_RIGHT" />
      </div>
    </button>
  );
};
